import React, { useEffect, useState } from "react";
import Shell from "@/components/Shell";
import api, { formatApiError, formatINR } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/pages/admin/AdminDashboard";
import { toast } from "sonner";
import { Pencil, Trash2, Check, X, Search, AlertTriangle, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";

const SORT_FIELDS = {
  invoice_date: { label: "Date", get: (i) => i.invoice_date },
  invoice_no: { label: "Invoice", get: (i) => i.invoice_no },
  customer_name: { label: "Customer", get: (i) => (i.customer_name || "").toLowerCase() },
  project: { label: "Project", get: (i) => (i.project || "").toLowerCase() },
  customer_ref_by_name: { label: "Ref by", get: (i) => (i.customer_ref_by_name || "").toLowerCase() },
  deal_closed_by_name: { label: "Closed by", get: (i) => (i.deal_closed_by_name || "").toLowerCase() },
  invoice_value: { label: "Value", get: (i) => Number(i.invoice_value) || 0 },
  margin: { label: "Margin", get: (i) => Number(i.margin) || 0 },
  status: { label: "Status", get: (i) => i.status },
};

export default function AdminInvoices() {
  const [tab, setTab] = useState("all");
  const [items, setItems] = useState([]);
  const [salespeople, setSalespeople] = useState([]);
  const [editing, setEditing] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [reason, setReason] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Filter / sort state
  const [search, setSearch] = useState("");
  const [filterRef, setFilterRef] = useState("__all__");
  const [filterClose, setFilterClose] = useState("__all__");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [sortField, setSortField] = useState("invoice_date");
  const [sortDir, setSortDir] = useState("desc");

  const [missing, setMissing] = useState([]);

  const load = () => {
    const url = tab === "all" ? "/invoices" : `/invoices?status=${tab}`;
    api.get(url).then((r) => setItems(r.data));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab]);
  useEffect(() => {
    api.get("/salespeople").then((r) => setSalespeople(r.data));
    api.get("/invoices/missing-numbers").then((r) => setMissing(r.data)).catch(() => {});
  }, []);
  // Refresh missing-numbers when items change
  useEffect(() => {
    api.get("/invoices/missing-numbers").then((r) => setMissing(r.data)).catch(() => {});
  }, [items.length]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const sortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown size={12} className="inline ml-1 opacity-40" />;
    return sortDir === "asc" ? <ArrowUp size={12} className="inline ml-1" /> : <ArrowDown size={12} className="inline ml-1" />;
  };

  const clearFilters = () => {
    setSearch(""); setFilterRef("__all__"); setFilterClose("__all__");
    setFilterFrom(""); setFilterTo("");
  };

  const filtered = (() => {
    const q = search.trim().toLowerCase();
    let arr = items.filter((i) => {
      if (q) {
        const hay = `${i.invoice_no} ${i.customer_name} ${i.project || ""} ${i.customer_ref_by_name} ${i.deal_closed_by_name}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (filterRef !== "__all__" && i.customer_ref_by_id !== filterRef) return false;
      if (filterClose !== "__all__" && i.deal_closed_by_id !== filterClose) return false;
      if (filterFrom && (i.invoice_date?.slice(0, 10) || "") < filterFrom) return false;
      if (filterTo && (i.invoice_date?.slice(0, 10) || "") > filterTo) return false;
      return true;
    });
    const cmp = SORT_FIELDS[sortField]?.get || (() => 0);
    arr = [...arr].sort((a, b) => {
      const av = cmp(a); const bv = cmp(b);
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  })();

  const save = async () => {
    try {
      const payload = {
        invoice_date: editing.invoice_date.slice(0, 10),
        invoice_no: editing.invoice_no,
        customer_name: editing.customer_name,
        project: editing.project || "",
        invoice_value: parseFloat(editing.invoice_value),
        purchase_value: parseFloat(editing.purchase_value),
        customer_ref_by_id: editing.customer_ref_by_id,
        deal_closed_by_id: editing.deal_closed_by_id,
      };
      // margin override
      if (editing._marginOverrideEnabled) {
        payload.margin_override = parseFloat(editing.margin_override);
      } else if (editing.margin_override !== null && editing.margin_override !== undefined) {
        payload.clear_margin_override = true;
      }
      // split override
      if (editing._splitOverrideEnabled) {
        const ro = parseFloat(editing._refPct) / 100;
        const co = parseFloat(editing._closePct) / 100;
        payload.referral_share_override = ro;
        payload.closure_share_override = co;
      } else if (editing.referral_share_override !== null || editing.closure_share_override !== null) {
        payload.clear_split_override = true;
      }
      await api.put(`/invoices/${editing.id}`, payload);
      toast.success("Invoice updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const openEdit = (inv) => {
    setEditing({
      ...inv,
      _marginOverrideEnabled: inv.margin_override !== null && inv.margin_override !== undefined,
      _splitOverrideEnabled:
        inv.referral_share_override !== null && inv.referral_share_override !== undefined,
      _refPct: inv.referral_share_override != null ? Math.round(inv.referral_share_override * 100) : 20,
      _closePct: inv.closure_share_override != null ? Math.round(inv.closure_share_override * 100) : 80,
      margin_override: inv.margin_override != null ? inv.margin_override : inv.computed_margin,
    });
  };

  const remove = async (inv) => {
    setDeleteTarget(inv);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/invoices/${deleteTarget.id}`);
      toast.success(`Invoice #${deleteTarget.invoice_no} deleted`);
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const approve = async (inv) => {
    try { await api.post(`/invoices/${inv.id}/approval`, { action: "approve" }); toast.success("Approved"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const submitReject = async () => {
    if (!reason.trim()) { toast.error("Reason required"); return; }
    try {
      await api.post(`/invoices/${rejectFor.id}/approval`, { action: "reject", rejection_reason: reason });
      toast.success("Rejected"); setRejectFor(null); setReason(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <Shell title="All Invoices" subtitle="Filter, sort, edit any invoice; gaps in invoice sequence are flagged.">
      {missing.some((m) => m.missing.length > 0) && (
        <Card className="p-4 mb-4 border-amber-200 bg-amber-50 shadow-none rounded-md" data-testid="missing-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle size={18} className="text-amber-700 mt-0.5" />
            <div className="flex-1 text-sm">
              <div className="font-semibold text-amber-900">Missing invoice numbers in sequence</div>
              <div className="mt-1 space-y-0.5 text-amber-800">
                {missing.filter((m) => m.missing.length > 0).map((m) => (
                  <div key={m.fiscal_year} data-testid={`missing-fy-${m.fiscal_year}`}>
                    <span className="font-semibold">FY {m.fiscal_year}:</span>{" "}
                    {m.missing_formatted.join(", ")}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>
      )}

      <Card className="p-4 mb-4 border-zinc-200 shadow-none rounded-md bg-white">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
          <div className="md:col-span-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Search</Label>
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Customer, invoice, project…" className="pl-8" data-testid="filter-search" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Ref by</Label>
            <Select value={filterRef} onValueChange={setFilterRef}>
              <SelectTrigger data-testid="filter-ref"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All</SelectItem>
                {salespeople.map((sp) => <SelectItem key={sp.id} value={sp.id}>{sp.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Closed by</Label>
            <Select value={filterClose} onValueChange={setFilterClose}>
              <SelectTrigger data-testid="filter-close"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All</SelectItem>
                {salespeople.map((sp) => <SelectItem key={sp.id} value={sp.id}>{sp.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">From</Label>
            <Input type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} data-testid="filter-from" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">To</Label>
            <Input type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} data-testid="filter-to" />
          </div>
        </div>
        <div className="flex items-center justify-between mt-3">
          <div className="text-xs text-zinc-500" data-testid="filter-count">
            Showing <span className="font-semibold text-zinc-700">{filtered.length}</span> of {items.length}
          </div>
          <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="filter-clear">Clear filters</Button>
        </div>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="all" data-testid="inv-tab-all">All</TabsTrigger>
          <TabsTrigger value="pending" data-testid="inv-tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="approved" data-testid="inv-tab-approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="inv-tab-rejected">Rejected</TabsTrigger>
        </TabsList>
        <TabsContent value={tab}>
          <Card className="border-zinc-200 shadow-none rounded-md overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-zinc-50">
                  <TableHead className="font-bold text-zinc-950 cursor-pointer select-none" onClick={() => toggleSort("invoice_no")} data-testid="sort-invoice_no">Invoice {sortIcon("invoice_no")}</TableHead>
                  <TableHead className="font-bold text-zinc-950 cursor-pointer select-none" onClick={() => toggleSort("invoice_date")} data-testid="sort-invoice_date">Date {sortIcon("invoice_date")}</TableHead>
                  <TableHead className="font-bold text-zinc-950 cursor-pointer select-none" onClick={() => toggleSort("customer_name")} data-testid="sort-customer_name">Customer {sortIcon("customer_name")}</TableHead>
                  <TableHead className="font-bold text-zinc-950 cursor-pointer select-none" onClick={() => toggleSort("project")} data-testid="sort-project">Project {sortIcon("project")}</TableHead>
                  <TableHead className="font-bold text-zinc-950 cursor-pointer select-none" onClick={() => toggleSort("customer_ref_by_name")} data-testid="sort-ref">Ref by {sortIcon("customer_ref_by_name")}</TableHead>
                  <TableHead className="font-bold text-zinc-950 cursor-pointer select-none" onClick={() => toggleSort("deal_closed_by_name")} data-testid="sort-close">Closed by {sortIcon("deal_closed_by_name")}</TableHead>
                  <TableHead className="font-bold text-zinc-950 text-right cursor-pointer select-none" onClick={() => toggleSort("invoice_value")} data-testid="sort-value">Value {sortIcon("invoice_value")}</TableHead>
                  <TableHead className="font-bold text-zinc-950 text-right cursor-pointer select-none" onClick={() => toggleSort("margin")} data-testid="sort-margin">Margin {sortIcon("margin")}</TableHead>
                  <TableHead className="font-bold text-zinc-950 cursor-pointer select-none" onClick={() => toggleSort("status")} data-testid="sort-status">Status {sortIcon("status")}</TableHead>
                  <TableHead className="font-bold text-zinc-950 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 && (
                  <TableRow><TableCell colSpan={10} className="text-center py-10 text-zinc-500">No invoices match your filters.</TableCell></TableRow>
                )}
                {filtered.map((inv) => (
                  <TableRow key={inv.id} data-testid={`admin-inv-row-${inv.id}`}>
                    <TableCell className="font-semibold font-mono text-xs">{inv.invoice_no}</TableCell>
                    <TableCell>{inv.invoice_date?.slice(0, 10)}</TableCell>
                    <TableCell>{inv.customer_name}</TableCell>
                    <TableCell className="text-zinc-600">{inv.project || <span className="text-zinc-400">—</span>}</TableCell>
                    <TableCell>{inv.customer_ref_by_name}</TableCell>
                    <TableCell>{inv.deal_closed_by_name}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatINR(inv.invoice_value)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatINR(inv.margin)}</TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <StatusBadge status={inv.status} />
                        {inv.rejection_reason && <div className="text-xs text-rose-700 italic">{inv.rejection_reason}</div>}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(inv)} data-testid={`edit-inv-${inv.id}`}><Pencil size={14} /></Button>
                        {inv.status === "pending" && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => approve(inv)} data-testid={`admin-approve-${inv.id}`}>
                              <Check size={14} className="text-blue-700" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => { setRejectFor(inv); setReason(""); }} data-testid={`admin-reject-${inv.id}`}>
                              <X size={14} className="text-rose-600" />
                            </Button>
                          </>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => remove(inv)} data-testid={`admin-delete-${inv.id}`}><Trash2 size={14} className="text-rose-600" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-xl" data-testid="edit-invoice-dialog">
          <DialogHeader><DialogTitle>Edit Invoice {editing && `#${editing.invoice_no}`}</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="font-semibold">Invoice Date</Label>
                <Input type="date" value={editing.invoice_date.slice(0, 10)} onChange={(e) => setEditing({ ...editing, invoice_date: e.target.value })} data-testid="edit-date" />
              </div>
              <div>
                <Label className="font-semibold">Invoice No</Label>
                <Input value={editing.invoice_no} onChange={(e) => setEditing({ ...editing, invoice_no: e.target.value })} data-testid="edit-no" />
              </div>
              <div className="md:col-span-2">
                <Label className="font-semibold">Customer Name</Label>
                <Input value={editing.customer_name} onChange={(e) => setEditing({ ...editing, customer_name: e.target.value })} data-testid="edit-customer" />
              </div>
              <div className="md:col-span-2">
                <Label className="font-semibold">Project / Product</Label>
                <Input value={editing.project || ""} onChange={(e) => setEditing({ ...editing, project: e.target.value })} data-testid="edit-project" />
              </div>
              <div>
                <Label className="font-semibold">Invoice Value (Pre-GST)</Label>
                <Input type="number" value={editing.invoice_value} onChange={(e) => setEditing({ ...editing, invoice_value: e.target.value })} data-testid="edit-value" />
              </div>
              <div>
                <Label className="font-semibold">Purchase Value</Label>
                <Input type="number" value={editing.purchase_value} onChange={(e) => setEditing({ ...editing, purchase_value: e.target.value })} data-testid="edit-purchase" />
              </div>
              <div>
                <Label className="font-semibold">Customer Ref By</Label>
                <Select value={editing.customer_ref_by_id} onValueChange={(v) => setEditing({ ...editing, customer_ref_by_id: v })}>
                  <SelectTrigger data-testid="edit-ref-by"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {salespeople.map((sp) => <SelectItem key={sp.id} value={sp.id}>{sp.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-semibold">Deal Closed By</Label>
                <Select value={editing.deal_closed_by_id} onValueChange={(v) => setEditing({ ...editing, deal_closed_by_id: v })}>
                  <SelectTrigger data-testid="edit-closed-by"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {salespeople.map((sp) => <SelectItem key={sp.id} value={sp.id}>{sp.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Margin override */}
              <div className="md:col-span-2 p-4 border border-zinc-200 rounded-md bg-zinc-50">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Margin</div>
                    <div className="text-sm text-zinc-600">Computed: {formatINR(editing.computed_margin)}</div>
                  </div>
                  <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!editing._marginOverrideEnabled}
                      onChange={(e) => setEditing({ ...editing, _marginOverrideEnabled: e.target.checked })}
                      data-testid="edit-margin-override-toggle"
                    />
                    Override margin
                  </label>
                </div>
                {editing._marginOverrideEnabled && (
                  <Input
                    type="number"
                    value={editing.margin_override ?? ""}
                    onChange={(e) => setEditing({ ...editing, margin_override: e.target.value })}
                    placeholder="Override margin (₹)"
                    data-testid="edit-margin-value"
                  />
                )}
              </div>

              {/* Split override */}
              <div className="md:col-span-2 p-4 border border-zinc-200 rounded-md bg-zinc-50">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Attribution split for THIS invoice</div>
                    <div className="text-sm text-zinc-600">Leave off to use the global default.</div>
                  </div>
                  <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!editing._splitOverrideEnabled}
                      onChange={(e) => setEditing({ ...editing, _splitOverrideEnabled: e.target.checked })}
                      data-testid="edit-split-override-toggle"
                    />
                    Override split
                  </label>
                </div>
                {editing._splitOverrideEnabled && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-semibold">Referral %</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={editing._refPct}
                        onChange={(e) => {
                          const n = Math.max(0, Math.min(100, parseFloat(e.target.value) || 0));
                          setEditing({ ...editing, _refPct: n, _closePct: 100 - n });
                        }}
                        data-testid="edit-ref-pct"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold">Closure %</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={editing._closePct}
                        onChange={(e) => {
                          const n = Math.max(0, Math.min(100, parseFloat(e.target.value) || 0));
                          setEditing({ ...editing, _closePct: n, _refPct: 100 - n });
                        }}
                        data-testid="edit-close-pct"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="edit-save-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rejectFor} onOpenChange={(o) => !o && setRejectFor(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Reject Invoice #{rejectFor?.invoice_no}</DialogTitle></DialogHeader>
          <Textarea placeholder="Reason (required)…" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="admin-reject-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectFor(null)}>Cancel</Button>
            <Button onClick={submitReject} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="admin-reject-confirm">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-invoice-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete invoice #{deleteTarget?.invoice_no}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the invoice for <span className="font-semibold">{deleteTarget?.customer_name}</span>. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-invoice-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="delete-invoice-confirm">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Shell>
  );
}
